import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  NO_REVIEW_FILTERS,
  hasActiveReviewFilters,
  rowMatchesReviewFilters,
} from '../utils/bulkLotNdcReviewFiltering';
import { summarizeReviewGroups } from '../utils/bulkLotNdcReviewGroups';
import useRecentlyMovedRowIds from './useRecentlyMovedRowIds';

/**
 * POM-6534 — the rows the grouped Review Lot & NDC grid shows, and the filters narrowing them.
 *
 * `reviewRows` is every reviewable row: the listed ones plus the No alternative rows the
 * classifier keeps out of `rows`. Filtering is display only — counts, exclusion and the printed
 * batch all keep reading the classifier's own output.
 */
const useGroupedReviewRows = ({ open, rows, unlistedRows, changes, unverifiedQty, bulkUndo }) => {
  const [filters, setFilters] = useState(NO_REVIEW_FILTERS);
  const [search, setSearch] = useState('');

  const clearFilters = useCallback(() => {
    setFilters(NO_REVIEW_FILTERS);
    setSearch('');
  }, []);

  // Every open starts unfiltered, as the review always has.
  useEffect(() => {
    if (open) clearFilters();
  }, [open, clearFilters]);

  const reviewRows = useMemo(() => [...rows, ...unlistedRows], [rows, unlistedRows]);

  const visibleRows = useMemo(
    () =>
      reviewRows.filter((row) =>
        rowMatchesReviewFilters({
          row,
          change: changes[row.item.requestLineItemId],
          filters,
          search,
        })
      ),
    [reviewRows, changes, filters, search]
  );

  // POM-6537 — `bulkUndo` rides along for the Pending commit header's "Undo bulk change".
  const groupSummaries = useMemo(
    () => ({
      ...summarizeReviewGroups({
        reviewRows,
        visibleRows,
        isFiltered: hasActiveReviewFilters({ filters, search }),
        unverifiedQty,
      }),
      bulkUndo: bulkUndo ?? null,
    }),
    [reviewRows, visibleRows, filters, search, unverifiedQty, bulkUndo]
  );

  const recentlyMovedIds = useRecentlyMovedRowIds(reviewRows);

  return {
    filters,
    setFilters,
    search,
    setSearch,
    clearFilters,
    reviewRows,
    visibleRows,
    groupSummaries,
    recentlyMovedIds,
  };
};

export default useGroupedReviewRows;
